import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  Fab,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useWarbandStore } from '../../stores/warbandStore';
import { ManageWizardsAndSpellsTab } from '../tabs/ManageWizardsAndSpellsTab';
import { ManageSoldiersTab } from '../tabs/ManageSoldiersTab';
import { ManageWarbandBaseTab } from '../tabs/ManageWarbandBaseTab';
import { ManageWarbandVaultTab } from '../tabs/ManageWarbandVaultTab';
import { IndexTab } from '../tabs/IndexTab';
import { SwipeTabs } from '../shared/SwipeTabs';
import { useSwipeTabs } from '../../hooks/useSwipeTabs';
import { useIceSurface } from '../../hooks/useIceSurface';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LockIcon from '@mui/icons-material/Lock';
import HouseIcon from '@mui/icons-material/House';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { createIceSurface } from '../../theme/surfaces';
import { computeUnusedLevelUps } from '../../utils/formatting';

export const ManageWarbandPage: React.FC = () => {
  const {
    isLoading,
    selectedWarbandId,
    lastManageDataWarbandId,
    manageTabIndex,
    setManageTabIndex,
    fetchManageWarbandData,
    openCreateSoldierModal,
    spellcastersAndSpells,
    warbands,
    levelUps,
    openAddSpellModal,
    openAddStartingSpellsModal,
    currentPage,
  } = useWarbandStore();

  const [resourcesModalOpen, setResourcesModalOpen] = useState(false);
  const [vaultModalOpen, setVaultModalOpen] = useState(false);

  const handleVaultClose = useCallback(() => setVaultModalOpen(false), []);
  const handleResourcesClose = useCallback(() => setResourcesModalOpen(false), []);

  const { containerRef, trackRef } = useSwipeTabs(manageTabIndex, 5, setManageTabIndex, currentPage === 'manage');

  // Memoised so SwipeTabs (React.memo) skips re-rendering the heavy tab trees
  // whenever only manageTabIndex changes (e.g. swipe / BottomNavigation tap).
  const panels = useMemo(() => [
    <ManageSoldiersTab key="soldiers" />,
    <ManageWizardsAndSpellsTab key="spells" />,
    <ManageWarbandVaultTab key="vault" vaultModalOpen={vaultModalOpen} onVaultModalClose={handleVaultClose} />,
    <ManageWarbandBaseTab key="base" resourcesModalOpen={resourcesModalOpen} onResourcesModalClose={handleResourcesClose} />,
    <IndexTab key="index" />,
  ], [vaultModalOpen, resourcesModalOpen, handleVaultClose, handleResourcesClose]);

  const spells = spellcastersAndSpells?.warbandSpells || [];
  const warband = warbands.find((w) => w.id === selectedWarbandId) ?? null;
  const unusedLevelUps = warband ? computeUnusedLevelUps(warband, levelUps) : 0;

  const fabConfig = (() => {
    switch (manageTabIndex) {
      case 0:
        return {
          label: 'Add Soldier',
          onClick: () => openCreateSoldierModal(false),
          sx: {},
          hidden: false,
          disabled: false,
        };
      case 1:
        return {
          label: spells.length === 0 ? 'Add Starting Spells' : unusedLevelUps > 0 ? 'Learn Spell' : 'Add Spell',
          onClick: spells.length === 0 ? openAddStartingSpellsModal : () => openAddSpellModal(unusedLevelUps > 0),
          sx: {
            backgroundColor: unusedLevelUps > 0 ? alpha('#FFD700', 0.55) : undefined,
            ':hover': { backgroundColor: unusedLevelUps > 0 ? alpha('#FFD700', 0.75) : undefined },
          },
          hidden: false,
          disabled: false,
        };
      case 2:
        return {
          label: 'Add to Vault',
          onClick: () => setVaultModalOpen(true),
          sx: {},
          hidden: false,
          disabled: !selectedWarbandId,
        };
      case 3:
        return {
          label: 'Add Resources',
          onClick: () => setResourcesModalOpen(true),
          sx: {},
          hidden: false,
          disabled: !selectedWarbandId,
        };
      default:
        return { label: '', onClick: () => {}, sx: {}, hidden: true, disabled: false };
    }
  })();

  useEffect(() => {
    if (selectedWarbandId && lastManageDataWarbandId !== selectedWarbandId) {
      fetchManageWarbandData(selectedWarbandId);
    }
  }, [selectedWarbandId, lastManageDataWarbandId, fetchManageWarbandData]);

  const tabsSurface = useIceSurface({ icicles: false, card: true });

  const iceSurface = createIceSurface(11);


  if (!selectedWarbandId) {
    return <Box>No party selected</Box>;
  }

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <SwipeTabs containerRef={containerRef} trackRef={trackRef} numTabs={5} panelPb={10}>
            {panels}
        </SwipeTabs>
      )}
      {!fabConfig.hidden && (
        <Fab
          variant="extended"
          color="primary"
          size="medium"
          onClick={fabConfig.onClick}
          disabled={fabConfig.disabled}
          sx={{ position: 'fixed', bottom: 72, right: 16, ...fabConfig.sx }}
        >
          <AddIcon sx={{ mr: 1 }} />
          {fabConfig.label}
        </Fab>
      )}
      <Paper sx={{ ...iceSurface, position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1 }} elevation={3} >
        <BottomNavigation
          sx={{
            ...tabsSurface,
          }}
          showLabels
          value={manageTabIndex}
          onChange={(event, newValue) => {
            setManageTabIndex(newValue);
          }}
        >
          <BottomNavigationAction label="Party" icon={<AccessibilityIcon />} />
          <BottomNavigationAction label="Spells" icon={<AutoFixHighIcon />} />
          <BottomNavigationAction label="Vault" icon={<LockIcon />} />
          <BottomNavigationAction label="Base" icon={<HouseIcon />} />
          <BottomNavigationAction label="Index" icon={<MenuBookIcon />} />
        </BottomNavigation>
      </Paper>
    </Box>
  );
};
